import React from 'react'
import { Paragraph } from './paragraph'
import { CardFullHeightScrollY } from '../../card/card-full-height-scroll-y'

// Our own cookie list, written by hand from the code (it replaced a consent
// service's generated declaration in October 2026). Keep it in step when
// something starts or stops storing in the browser:
// - the admin: src/storage/auth.js, shared/recent-accounts, shared/thumbnail-cache, ...
// - the public website: not here; it keeps its own list at educationxr.com/cookies,
//   generated from edxr-marketing's src/lib/third-parties.json
// - the documentation site: rapid-api/edxr-docs-server tools/cookie.go
// - the sign-in service: rapid-api-auth api/ddd/service/auth-provider/link-confirm-*.go

export const COOKIE_POLICY_UPDATED = 'October 26, 2026'

// The public website keeps its own list, made from the registry its consent banner uses.
const WEBSITE_COOKIES = 'https://educationxr.com/cookies'

// The admin site: everything here is needed for it to work.
const ADMIN_COOKIES = [
  {
    name: 'edxr-shared-device',
    what: 'Set only when you choose "Sign in privately" at sign-in, so the sign-in page does not remember your account on this device.',
    lasts: 'Until you close the browser'
  }
]

const ADMIN_SESSION_STORAGE = [
  { name: 'accessToken, refreshToken', what: 'Keep you signed in.', lasts: 'Until you sign out or close the tab' },
  { name: 'edxr-logout-reason', what: 'Lets the sign-in page say why you were signed out.', lasts: 'Read once by the sign-in page' },
  { name: 'edxr-pending-sign-in', what: 'Remembers which single sign-on connection a sign-in started with.', lasts: 'Until the sign-in finishes, at most an hour' },
  { name: 'lms-launch-signin', what: 'Ties a course launched from your learning system to the sign-in this tab started.', lasts: 'Used once, at most 15 minutes' },
  { name: 'edxr-invite-key', what: 'Keeps an invitation’s key out of the page address while you sign in to accept it.', lasts: 'Until the invitation is accepted or the tab is closed' },
  { name: 'edxr-org-restored-at', what: 'Keeps you signed in for a minute after your organization is restored.', lasts: 'Until you close the tab' },
  { name: 'credentialExpiryBannerDismissed', what: 'Remembers that you closed the sign-in certificate warning.', lasts: 'Until you close the tab' },
  { name: 'eab-dismissed', what: 'Remembers that you closed the "Get the app" banner.', lasts: 'Until you close the tab' }
]

const ADMIN_LOCAL_STORAGE = [
  {
    name: 'edxr-recent-accounts-v1',
    what: 'Up to five accounts for "Choose an account" at sign-in: email, name and how you signed in. Not kept on a shared device; remove them with "Forget" on the sign-in page.',
    lasts: 'Until you forget them'
  },
  { name: 'edxr-thumbnails-v1-index, edxr-thumbnails-clear', what: 'The index of the thumbnail cache below.', lasts: 'Cleared when you sign out' },
  { name: 'theme', what: 'Light, dark or system appearance.', lasts: 'Until you clear it' },
  { name: 'edxr.sso.lastOrganizationID', what: 'Selects the organization you last signed in to with single sign-on.', lasts: 'Until you clear it' },
  { name: 'edxr.deepLink.options', what: 'The options you last chose for a course link.', lasts: 'Until you clear it' },
  { name: 'edxr.sidebar.staffTools', what: 'Whether the staff tools are shown in the menu (EducationXR staff only).', lasts: 'Until you clear it' },
  { name: 'edxr-notify-web-device', what: 'If you turn on browser notifications, lets you turn them off again for this device.', lasts: 'Until you turn notifications off' },
  { name: 'edxr-lms-browser', what: 'A random number for this browser, sent when your learning system launches a course, to spot one browser used by several learners.', lasts: 'Until you clear it' },
  { name: 'edxr-admin-idle:…', what: 'Only in browsers that cannot message their other tabs directly: keeps the automatic sign-out after inactivity the same in every tab.', lasts: 'Deleted within a second' }
]

const ADMIN_CACHE_STORAGE = [
  {
    name: 'edxr-thumbnails-v1',
    what: 'Course and asset thumbnails, so they are not downloaded again: at most 500 pictures and 50 MB, the least recently used removed first.',
    lasts: 'Emptied when you sign out'
  }
]

// Set by our sign-in service on its own address, not the admin's.
const SIGN_IN_SERVICE_COOKIES = [
  {
    name: '__Host-edxr_link_proof, __Host-edxr_link_hold_…',
    what: 'Only while you connect a sign-in provider to an existing account: tie that step to the browser that started it.',
    lasts: '15 minutes'
  }
]

const DOCS_COOKIES = [
  {
    name: 'Documentation sign-in cookie',
    what: 'Set on docs.educationxr.com when you open the documentation from the admin, to let you read it. It holds no personal information.',
    lasts: '60 seconds'
  },
  { name: 'theme, recent and favorite searches (local storage)', what: 'The documentation site’s appearance and your searches on it.', lasts: 'Until you clear them' }
]

const ItemList = ({ items }) => (
  <ul>
    {items.map(item => (
      <li key={item.name}>
        <code>{item.name}</code>: {item.what} <span className="text-body-secondary">({item.lasts})</span>
      </li>
    ))}
  </ul>
)

export const Cookie = () => {
  return (
    <CardFullHeightScrollY title="cookie">
      <Paragraph header="COOKIE POLICY">
        <p>
          This page lists everything EducationXR keeps in your browser on the admin site (this site) and the documentation site, and
          why. Our public website keeps its own list (below). Last updated {COOKIE_POLICY_UPDATED}.
        </p>
      </Paragraph>

      <Paragraph header="THE ADMIN SITE">
        <p>
          Everything the admin site keeps in your browser is needed for it to work. It sets no analytics or advertising cookies, and
          nothing here is used to follow you across other sites.
        </p>

        <p>Cookies:</p>
        <ItemList items={ADMIN_COOKIES} />

        <p>In this tab only (session storage, removed when you close the tab):</p>
        <ItemList items={ADMIN_SESSION_STORAGE} />

        <p>On this device (local storage):</p>
        <ItemList items={ADMIN_LOCAL_STORAGE} />

        <p>Cache storage:</p>
        <ItemList items={ADMIN_CACHE_STORAGE} />

        <p>Set by our sign-in service, on its own address:</p>
        <ItemList items={SIGN_IN_SERVICE_COOKIES} />

        <p>
          If you turn on browser notifications, your browser also keeps the notification permission and a notification subscription for
          this site until you turn them off.
        </p>
      </Paragraph>

      <Paragraph header="SIGN-UP PAGES FOR EVENTS">
        <p>
          The event sign-up pages (addresses that start with /external/) run a security check that tells people apart from automated
          sign-ups. The check’s provider sets its own cookie and may keep a short entry in this site’s storage. It is needed to block
          automated sign-ups, so it runs on those pages without asking.
        </p>
      </Paragraph>

      <Paragraph header="THE DOCUMENTATION SITE">
        <ItemList items={DOCS_COOKIES} />
      </Paragraph>

      <Paragraph header="OUR PUBLIC WEBSITE">
        <p>
          Our public website, educationxr.com, asks for your choice with its own cookie banner and keeps the full list of its cookies,
          what each is for and how long it lasts, made from the same list the banner uses:{' '}
          <a href={WEBSITE_COOKIES} target="_blank" rel="noopener noreferrer">
            educationxr.com/cookies
          </a>
          . To change your choice there, use “Cookie settings” at the bottom of any of its pages.
        </p>
      </Paragraph>

      <Paragraph header="CLEARING WHAT IS STORED">
        <p>
          You can remove all of the above in your browser’s settings. On the admin site that signs you out, and the next visit starts
          fresh. Questions: <a href="mailto:privacy@educationxr.com">privacy@educationxr.com</a>.
        </p>
      </Paragraph>
    </CardFullHeightScrollY>
  )
}
